import { lazy, type ComponentType } from 'react';

const RELOAD_KEY = 'zt-stale-chunk-reload';
const RELOAD_COOLDOWN_MS = 15_000;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|Loading chunk .+ failed/i.test(
    message,
  );
}

/**
 * Full-reload once after a stale Vite chunk/CSS preload. A cooldown prevents
 * a reload loop if the new document still can't load assets.
 * Returns true if a reload was triggered.
 */
export function reloadOnceForStaleAssets(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || '0');
    const now = Date.now();
    if (now - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // sessionStorage can throw in private mode; still try to recover.
  }
  window.location.reload();
  return true;
}

export function installVitePreloadReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadOnceForStaleAssets()) {
      event.preventDefault();
    }
  });
}

export function lazyWithRetry(importer: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(async () => {
    try {
      return await importer();
    } catch (error) {
      if (isChunkLoadError(error) && reloadOnceForStaleAssets()) {
        return new Promise<{ default: ComponentType<any> }>(() => {});
      }
      throw error;
    }
  });
}
